import React, { memo } from 'react';
import PropTypes from 'prop-types';
import { makeStyles } from '@material-ui/core/styles';
import { FastField, Field, FieldArray, Form, Formik } from 'formik';
import { object, string } from 'yup';
import get from 'lodash/get';
import { connect, useDispatch } from 'react-redux';
import { createStructuredSelector } from 'reselect';
import last from 'lodash/last';
import { compact, debounce } from 'lodash';
import { mapValues } from 'lodash/object';
import { useTranslation } from '../../src/i18n';
import { AOS_TYPE_ID, PRIVACY_ATTRIBUTE_IDS, TP, VISIBILITY } from '../../constants/consts';
import FormikTextField from '../../components/Fields/TextField';
import FormikAutocompleteSelect from '../../components/Fields/FormikAutocompleteSelect';
import SubmitModalLayout from '../../components/SubmitModalLayout';
import { EditModeCardSection } from '../../components/EditModeCard';
import FormikPhoneField from '../../components/Fields/FormikPhoneField';
import AddAnotherField from '../../components/AddAnotherField';
import { createEntity, getAOSProfessionsAction, getList, getWholeList, updateOne } from '../App/actions';
import { AOS_PROFESSIONS, FESTIVAL_PROFESSIONS, PROFILES_ENDPOINT } from '../../constants/endpointConstants';
import { selectAOSProfessionsOptions } from '../App/selectors';
import { getOption } from '../../utils/common';
import { addStaffAction, getStaffSuggestionsAction, updateStaffAction } from './actions';
import { selectStaffSuggestionsLoading, selectStaffSuggestionsOptions } from './selectors';
import { EDITING_ENTITY_TYPES } from '../../utils/editCommon';
import { entityFields } from './constants';

const useStyles = makeStyles(theme => ({
  root: {
    width: 504,
    left: 0,
    marginLeft: 'auto',
    marginRight: 'auto',
    [theme.breakpoints.down('xs')]: {
      width: '100%',
      maxWidth: 504,
    },
  },
  field: {
    maxWidth: 260,
  },
}));

const getFields = type => ({
  name: {
    name: entityFields[type]?.name,
    label: `${TP}.FULL_NAME`,
    value: undefined,
  },
  companyEmployeeProfession: {
    name: entityFields[type]?.employeeProfession,
    label: `${TP}.m_JOBTITLE`,
    value: undefined,
  },
  email: {
    name: entityFields[type]?.email,
    label: `${TP}.m_EMAIL`,
    value: undefined,
  },
  phones: {
    name: entityFields[type]?.phones,
    label: `${TP}.m_PHONENUMBER`,
    value: [undefined],
  },
});

const StaffModal = ({
  staff,
  onClose,
  professions,
  staffSuggestionsOptions,
  staffSuggestionsLoading,
  companyId,
  staffEntityEndpoint,
  type,
}) => {
  const { name, companyEmployeeProfession, email, phones } = getFields(type);
  const classes = useStyles();
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const formRef = React.createRef(null);

  React.useEffect(() => {
    if (professions?.length === 0) {
      dispatch(
        getWholeList({
          endpoint: type === EDITING_ENTITY_TYPES.COMPANY ? AOS_PROFESSIONS : FESTIVAL_PROFESSIONS,
          sagaRoutine: getAOSProfessionsAction,
        }),
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const initialValues = {
    [name.name]: getOption(staff?.profile) || name.value,
    [companyEmployeeProfession.name]: get(staff, companyEmployeeProfession.name)
      ? getOption(get(staff, companyEmployeeProfession.name))
      : companyEmployeeProfession.value,
    [email.name]: get(staff, email.name, email.value) || email.value,
    [phones.name]: compact(get(staff, phones.name, phones.value)) || phones.value,
  };

  const getProfileSuggestions = debounce(query => {
    dispatch(
      getList({
        endpoint: PROFILES_ENDPOINT,
        sagaRoutine: getStaffSuggestionsAction,
        params: {
          queryParams: { profile_type_id: AOS_TYPE_ID, query, show_invalid: true, visibility: VISIBILITY.ALL },
        },
      }),
    );
  }, 300);

  const getValueFromSelectOption = val => {
    const getValue = v => (v?.value && typeof v?.value !== 'string' ? { id: v.value } : { name: v?.label });
    return val?.value || val?.label ? getValue(val) : val;
  };

  const submitForm = formValues => {
    const updatedStaff = mapValues(formValues, getValueFromSelectOption);
    if (staff?.id) {
      dispatch(
        updateOne({
          endpoint: staffEntityEndpoint(staff?.id),
          sagaRoutine: updateStaffAction,
          params: {
            ...updatedStaff,
            ...{
              id: staff.id,
              privacyAttributes:
                staff?.privacyAttributes?.length > 0 ? staff?.privacyAttributes : [{ id: PRIVACY_ATTRIBUTE_IDS.PRO }],
            },
          },
          callback: onClose,
        }),
      );
    } else {
      dispatch(
        createEntity({
          endpoint: staffEntityEndpoint(),
          sagaRoutine: addStaffAction,
          params: {
            ...updatedStaff,
            ...{
              privacyAttributes: [{ id: PRIVACY_ATTRIBUTE_IDS.PRO }],
              [entityFields[type]?.entity]: { id: companyId },
            },
          },
          callback: onClose,
        }),
      );
    }
  };

  return (
    <SubmitModalLayout
      isOpened
      onClose={onClose}
      title1={t(`${TP}.FN_ADD_CONTACT_DETAILS`)}
      submitFunc={() => {
        get(formRef?.current, 'submitForm', () => {})();
      }}
      className={classes.root}
    >
      <Formik
        initialValues={initialValues}
        onSubmit={submitForm}
        innerRef={formRef}
        validationSchema={() =>
          object().shape({
            [name.name]: object()
              .nullable()
              .required(`${TP}.FN_REQUIRED_FIELD`),
            [email.name]: string()
              .nullable()
              .email(`${TP}.m_MAILNONVALID`),
          })
        }
        enableReinitialize
      >
        {({ values }) => (
          <Form className={classes.scrollable}>
            <EditModeCardSection>
              <Field
                t={t}
                name={name.name}
                component={FormikAutocompleteSelect}
                label={t(name.label)}
                className={classes.field}
                options={staffSuggestionsOptions}
                instanceId={name.name}
                isClearable
                minSearchCharsLength={3}
                onInputTextChange={getProfileSuggestions}
                isLoading={staffSuggestionsLoading}
                isCreatable
              />
            </EditModeCardSection>
            <EditModeCardSection>
              <Field
                t={t}
                name={companyEmployeeProfession.name}
                component={FormikAutocompleteSelect}
                label={t(companyEmployeeProfession.label)}
                options={professions}
                instanceId={companyEmployeeProfession.name}
                isClearable
                withFiltering
                className={classes.field}
                isCreatable
              />
            </EditModeCardSection>
            <EditModeCardSection>
              <Field name={email.name} component={FormikTextField} label={t(email.label)} className={classes.field} />
            </EditModeCardSection>
            <EditModeCardSection>
              <FieldArray name={phones.name}>
                {arrayHelpers => {
                  const parentName = phones.name;
                  const phoneValues = values[parentName].length ? values[parentName] : phones.value;
                  return phoneValues.map((field, index) => {
                    const fieldName = `${parentName}.${index}`;
                    return (
                      <AddAnotherField
                        key={fieldName}
                        values={phoneValues}
                        removeFunc={() => arrayHelpers.remove(index)}
                        addFunc={() => arrayHelpers.push(undefined)}
                        isLast={index === phoneValues.length - 1}
                        addDisabled={!last(phoneValues)?.length}
                        inputClassName={classes.field}
                      >
                        <FastField name={fieldName} component={FormikPhoneField} label={index ? '' : t(phones.label)} />
                      </AddAnotherField>
                    );
                  });
                }}
              </FieldArray>
            </EditModeCardSection>
          </Form>
        )}
      </Formik>
    </SubmitModalLayout>
  );
};

StaffModal.propTypes = {
  staff: PropTypes.object,
  onClose: PropTypes.func.isRequired,
  professions: PropTypes.array.isRequired,
  companyId: PropTypes.number.isRequired,
  type: PropTypes.oneOf([EDITING_ENTITY_TYPES.FESTIVAL, EDITING_ENTITY_TYPES.COMPANY]),
};

const mapStateToProps = createStructuredSelector({
  staffSuggestionsOptions: selectStaffSuggestionsOptions(),
  staffSuggestionsLoading: selectStaffSuggestionsLoading(),
  professions: selectAOSProfessionsOptions(),
});

const withConnect = connect(mapStateToProps, null);

export default withConnect(memo(StaffModal));
